import { matchPath, useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';
import { ConsentManagerBuilder } from '@sorare/cookie-consent-manager';

import { Popup } from '#src/atoms/layout/Popup/index.tsx';
import { IS_PRERENDER, SEGMENT_API_KEY } from '#src/config.ts';
import {
  ANY_SPORT_INVITE_MODE_SLUG,
  COOKIE_POLICY,
} from '#src/constants/__generated__/routes.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { matchAnySportPath } from '#src/lib/routing/generateSportPath.ts';

import { DetailedConsent, SimpleConsent } from './Consent.tsx';

declare global {
  interface Window {
    segmentApiKey: string;
  }
}

const DISABLED_LOCATIONS = [
  // this pages needs to be accessible without cookies/consent modal
  COOKIE_POLICY,
];

const DISABLED_ANY_SPORT_LOCATIONS = [
  // the invite landing page must stay unobstructed for the invited visitor
  ANY_SPORT_INVITE_MODE_SLUG,
];

const ALLOWED_MOBILE_APP_DESTINATION_IDS = [
  /amplitude/i,
  /appboy/i, // Braze
  /braze/i,
];

export const CookieConsentBanner = () => {
  const { isMobileApp, consentTracking } = useIsMobileApp();
  const [detailsOpened, toggleDetails] = useToggle(false);
  const [, setConsent] = useLocalStorage('trackingConsent', false);

  const location = useLocation();

  if (!SEGMENT_API_KEY) {
    return null;
  }

  if (IS_PRERENDER) {
    // Do not load ads/trackers for prerender
    return null;
  }

  if (DISABLED_LOCATIONS.find(path => matchPath(path, location.pathname))) {
    // no banner on this page
    return null;
  }

  if (
    DISABLED_ANY_SPORT_LOCATIONS.find(path =>
      matchAnySportPath(catchAll(path), location.pathname)
    )
  ) {
    return null;
  }

  return (
    <ConsentManagerBuilder
      writeKey={SEGMENT_API_KEY}
      // 6 month (but safari limits it to 7 days)
      cookieExpires={183}
      mobileAppDestinations={
        isMobileApp && consentTracking
          ? ALLOWED_MOBILE_APP_DESTINATION_IDS
          : undefined
      }
    >
      {args => {
        const {
          isConsentRequired,
          destinations,
          newDestinations,
          preferences,
          setPreferences,
          saveConsent,
        } = args;

        if (newDestinations.length === 0 || !isConsentRequired) {
          return null;
        }

        const saveConsentWrapper = (value?: boolean) => {
          const newPreferences = Object.fromEntries(
            destinations.map(d => [d.id, value ?? !!preferences[d.id]])
          );
          setPreferences(newPreferences);
          setConsent(value !== false);
          if (value !== false) {
            // TODO(su): if this is working, expose within the details modal
            window.analytics.ready(() => {
              if (window.gtag) {
                window.gtag('consent', 'update', {
                  ad_storage: 'granted',
                  ad_user_data: 'granted',
                  ad_personalization: 'granted',
                  analytics_storage: 'granted',
                });
              }
            });
          }
          saveConsent(newPreferences);
        };
        return (
          <Popup>
            {detailsOpened ? (
              <DetailedConsent
                {...{
                  destinations,
                  preferences,
                  setPreferences,
                  saveConsent: saveConsentWrapper,
                }}
              />
            ) : (
              <SimpleConsent
                saveConsent={saveConsentWrapper}
                seeDetails={() => {
                  setPreferences(
                    Object.fromEntries(destinations.map(d => [d.id, true]))
                  );
                  toggleDetails();
                }}
              />
            )}
          </Popup>
        );
      }}
    </ConsentManagerBuilder>
  );
};
